import type { ReactNode } from "react";

export type ToolbarState = "NO_SHIFT" | "WORKING" | "ON_LUNCH";

export interface AppChromeProps {
  role: "DRIVER" | "DISPATCHER";
  toolbarState: ToolbarState;
  onLunchOut: () => void;
  onLunchIn: () => void;
  onClockOut: () => void;
  overtime?: boolean;
  showClockOut?: boolean;
  /** SPEC-65: the day's lunch allowance is used — Lunch disabled. */
  lunchTaken?: boolean;
  children: ReactNode;
}

/** Shared logo/header and punch toolbar; punch behavior stays with the parent. */
export default function AppChrome({
  role,
  toolbarState,
  onLunchOut,
  onLunchIn,
  onClockOut,
  overtime = false,
  showClockOut = true,
  lunchTaken,
  children,
}: AppChromeProps) {
  const onLunch = toolbarState === "ON_LUNCH" ? onLunchIn : onLunchOut;
  const lunchLabel = toolbarState === "ON_LUNCH" ? "END LUNCH" : "Lunch";
  // SPEC-65 (audit r2 BUG #1): the button is dual-purpose (start new / END
  // current). Block only a NEW lunch attempt — END LUNCH must stay tappable
  // while a lunch is open (ON_LUNCH), even though lunchTaken is true.
  const lunchDisabled =
    toolbarState === "NO_SHIFT" ||
    (lunchTaken === true && toolbarState !== "ON_LUNCH");
  // Title priority (audit r3 ADVISORY #1): at NO_SHIFT+flag the missing
  // shift is the proximate cause — "Clock in first" wins.
  const lunchTitle = lunchDisabled
    ? (toolbarState === "NO_SHIFT" ? "Clock in first" : "Lunch already taken")
    : undefined;
  const lunchClass = `toolbar-lunch${overtime && toolbarState === "ON_LUNCH" ? " overtime" : ""}`;

  return (
    <div className="app-chrome">
      <header className="chrome-header">
        <img
          className="chrome-logo"
          src="/eagle-dispatch-logo.jpg"
          alt="Eagle Dispatch"
        />
      </header>
      <main className="chrome-content">{children}</main>
      <footer className={`chrome-toolbar chrome-toolbar--${role.toLowerCase()}`}>
        <button
          type="button"
          className={lunchClass}
          onClick={onLunch}
          disabled={lunchDisabled}
          title={lunchTitle}
        >
          {lunchLabel}
        </button>
        {role === "DISPATCHER" && (
          <>
            <button type="button" className="chrome-nav-button">Home</button>
            <button type="button" className="chrome-nav-button">Jobs</button>
          </>
        )}
        {showClockOut && (
          <button type="button" className="toolbar-clock-out" onClick={onClockOut}>
            Clock Out
          </button>
        )}
      </footer>
    </div>
  );
}
